import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { PropTypes } from 'prop-types';
import Chart from 'react-apexcharts';
import fileDownload from 'js-file-download';
import { format } from 'date-fns';
import { withStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { StoreUtil, PatientType, LocationTypeUtil } from 'doctivity-shared/utils';
import { LoadingView } from './LoadingView';
import { TitleTooltip } from './TitleTooltip';
import { PatientTypeSelect } from './PatientTypeSelect';
import {
  loadTrendingData,
  trendingKey,
  TrendingDataType,
} from 'store/actions/trendingActions';
import { listClaimsServicelines } from 'store/actions/claimsActions';
import CLAIMS_CFG from '../../config/claims-date.js';

const TOP_10 = 'top10';
const ALL = 'all';
const TOP_10_COUNT = 10;

// app patientType -> which per-quarter file the trending store fetches
const DATA_TYPE_BY_PATIENT_TYPE = {
  [PatientType.ALL_PATIENTS]: TrendingDataType.PROJECTED_PATIENTS,
  [PatientType.PEDIATRIC_PATIENTS]: TrendingDataType.PEDIATRIC_PROJECTED_PATIENTS,
  [PatientType.CAPTURED_CLAIMS]: TrendingDataType.CAPTURED_CLAIMS,
};

const METRIC_LABELS = {
  [PatientType.ALL_PATIENTS]: 'Projected Patients',
  [PatientType.PEDIATRIC_PATIENTS]: 'Pediatric Projected Patients',
  [PatientType.CAPTURED_CLAIMS]: 'Captured Claims',
};

// LexisNexis small-cell suppression arrives as a null count; the UI shows it
// as "<11" everywhere (see ProviderProjectedPatientsChart.formatMetric).
const SUPPRESSED_COUNT = '<11';

// Quote a CSV field only when it needs it (commas, quotes, newlines) —
// CsvUtil.matrixToString does not escape, so don't use it here.
function csvField(value) {
  const str = value === null || value === undefined ? '' : String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

const styles = (theme) => ({
  filters: {
    display: 'flex',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  filter: {
    marginRight: theme.spacing(),
    minWidth: 168,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
  },
  italicOption: {
    fontStyle: 'italic',
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.primary.main,
  },
  chartWrap: {
    position: 'relative',
  },
  // value labels drawn over the chart for the hovered legend entry; they never
  // intercept the mouse, so the chart and legend keep working underneath
  pointDot: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.background.paper}`,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  },
  pointLabel: {
    position: 'absolute',
    padding: '2px 8px',
    borderRadius: 12,
    border: '1px solid',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '18px',
    whiteSpace: 'nowrap',
    boxShadow: theme.shadows[1],
    pointerEvents: 'none',
    // sits centred just above its point
    transform: 'translate(-50%, calc(-100% - 8px))',
  },
});

// Every quarter from trendingStartQuarter through trendingEndQuarter
// (config/claims-date.js). Each quarter's value is LexisNexis's trailing
// 12 months of data ending in that quarter.
function trendingQuarters() {
  const { trendingStartQuarter: start, trendingEndQuarter: end } = CLAIMS_CFG;
  const quarters = [];
  let { year, quarter } = start;
  while (year < end.year || (year === end.year && quarter <= end.quarter)) {
    quarters.push({ year, quarter });
    quarter++;
    if (quarter > 4) {
      quarter = 1;
      year++;
    }
  }
  return quarters;
}

function ServicelineTrendsChart({ classes, entityType, entity }) {
  const dispatch = useDispatch();

  // The tree is keyed by NPI, falling back to the LexisNexis claims id for
  // NPI-less entities (same convention the emitter uses).
  const entityKey =
    entity?.npi || entity?.claims_hms_poid || entity?.claims_hms_piid || null;

  const quarters = useMemo(() => trendingQuarters(), []);

  // serviceline filter: 'top10' (default) | 'all' | a top-level serviceline id
  const [servicelineSel, setServicelineSel] = useState(TOP_10);
  // Metric locked to Projected Patients for now. When the patient-type
  // dropdown comes back (commented out below), restore the app-state line so
  // it follows the global PatientTypeSelect like Market Data does:
  // const patientType = useSelector((state) => state.app.patientType);
  const patientType = PatientType.ALL_PATIENTS;
  const dataType =
    DATA_TYPE_BY_PATIENT_TYPE[patientType] || TrendingDataType.PROJECTED_PATIENTS;
  const metricLabel = METRIC_LABELS[patientType] || METRIC_LABELS[PatientType.ALL_PATIENTS];

  const servicelines = useSelector(
    (state) => state.claims && state.claims.servicelines_grouped,
    shallowEqual,
  );
  const servicelinesList = useSelector(
    (state) => StoreUtil.get(state.claims, 'servicelines_list'),
    shallowEqual,
  );

  const keys = useMemo(
    () =>
      quarters.map((q) =>
        trendingKey(entityType, entityKey, q.year, q.quarter, dataType),
      ),
    [entityType, entityKey, quarters, dataType],
  );
  const entries = useSelector(
    (state) => keys.map((k) => state.trending.entries[k]),
    shallowEqual,
  );

  useEffect(() => {
    if (!servicelines && StoreUtil.needsLoadNoCache(servicelinesList)) {
      dispatch(listClaimsServicelines());
    }
  }, [dispatch, servicelines, servicelinesList]);

  useEffect(() => {
    if (!entityKey) return;
    quarters.forEach((q) => {
      dispatch(
        loadTrendingData(entityType, entityKey, q.year, q.quarter, dataType),
      );
    });
  }, [dispatch, entityType, entityKey, quarters, dataType]);

  // top-level standard servicelines with slugs, for the dropdown + grouping
  const parents = useMemo(
    () =>
      (servicelines || [])
        .filter((s) => !s.client_id && s.slug)
        .slice()
        .sort((a, b) => (a.name < b.name ? -1 : 1)),
    [servicelines],
  );
  const selectedParent =
    servicelineSel !== TOP_10 && servicelineSel !== ALL
      ? parents.find((p) => p.id === servicelineSel)
      : null;

  // only offer top-level servicelines this entity actually has rows for
  // (presence-based: a serviceline that's fully suppressed — all <10 counts,
  // null totals — still has rows and stays selectable)
  const availableParents = useMemo(() => {
    const seen = new Set();
    entries.forEach((entry) => {
      if (!entry || !entry.loaded || !entry.data) return;
      entry.data.forEach((row) => seen.add(row.serviceline));
    });
    return parents.filter((p) => seen.has(p.slug));
  }, [entries, parents]);

  // if the metric/entity changes and the selected serviceline has no data
  // under the new view, fall back to Top 10 rather than an empty chart
  useEffect(() => {
    if (
      selectedParent &&
      availableParents.length > 0 &&
      !availableParents.some((p) => p.id === selectedParent.id)
    ) {
      setServicelineSel(TOP_10);
    }
  }, [availableParents, selectedParent]);

  const { series, categories, anyData, noChildren } = useMemo(() => {
    const categories = quarters.map((q) => `${q.year} Q${q.quarter}`);

    // which servicelines become lines: all parents, or one parent's children
    let lines;
    let noChildren = false;
    if (selectedParent) {
      lines = (selectedParent.children || []).filter(
        (c) => !c.client_id && c.slug,
      );
      noChildren = lines.length === 0;
    } else {
      lines = parents;
    }
    const totalsBySlug = new Map(lines.map((l) => [l.slug, []]));

    // a quarter counts as PRESENT when its file loaded with rows; a missing /
    // not-yet-uploaded quarter (cached 404 -> empty) is a data gap, not a zero
    const quarterPresent = entries.map(
      (entry) => !!(entry && entry.loaded && entry.data && entry.data.length > 0),
    );

    entries.forEach((entry, qi) => {
      if (!entry || !entry.loaded || !entry.data) return;
      entry.data.forEach((row) => {
        // rows exist for both parent and sub servicelines; the lines map only
        // matches whichever level is being charted, so there's no double count
        const totals = totalsBySlug.get(row.serviceline);
        if (totals) {
          totals[qi] = (totals[qi] || 0) + (row.total_patients || 0);
        }
      });
    });

    let series = lines
      .map((line) => {
        const totals = totalsBySlug.get(line.slug);
        // missing quarters hold the previous quarter's value flat instead of
        // plunging to 0; a PRESENT quarter without this serviceline is a real
        // 0 and still shows as one. Leading gaps (before the first present
        // quarter) stay null so the line starts at the first real data point.
        let carried = null;
        const data = quarters.map((q, qi) => {
          if (quarterPresent[qi]) {
            carried = totals[qi] || 0;
            return carried;
          }
          return carried; // null before any data; flat afterwards
        });
        const total = data.reduce((sum, v) => sum + (v || 0), 0);
        return { slug: line.slug, name: line.name, data, total };
      })
      // only chart servicelines this entity actually has data for
      .filter((s) => s.data.some((v) => v > 0));

    if (servicelineSel === TOP_10) {
      series = series
        .slice()
        .sort((a, b) => b.total - a.total)
        .slice(0, TOP_10_COUNT);
    }
    // slugs of the servicelines actually drawn, so the CSV export matches the chart
    const chartedSlugs = new Set(series.map((s) => s.slug));
    series = series.map(({ name, data }) => ({ name, data }));

    return {
      series,
      categories,
      anyData: series.length > 0,
      noChildren,
      chartedSlugs,
    };
  }, [entries, parents, selectedParent, servicelineSel, quarters]);

  // slug -> display names, for both parent and sub servicelines
  const servicelineNames = useMemo(() => {
    const names = new Map();
    parents.forEach((p) => {
      names.set(p.slug, { serviceline: p.name, subServiceline: '' });
      (p.children || []).forEach((c) => {
        if (c.slug) {
          names.set(c.slug, { serviceline: p.name, subServiceline: c.name });
        }
      });
    });
    return names;
  }, [parents]);

  const chartRef = useRef(null);
  // the legend entry being hovered + its on-chart value labels (see below)
  const [legendHover, setLegendHover] = useState(null);
  const chartWrapRef = useRef(null);

  // Memoized so hovering the legend (a state change) doesn't hand apex a new
  // options object and make it rebuild the chart + legend under the mouse.
  const chartOptions = useMemo(
    () => ({
      chart: {
        id: 'ServicelineTrends',
        toolbar: { show: false },
        animations: { enabled: false },
        events: {
          // keep the apex instance: the legend hover reads point positions off it
          mounted: (chart) => {
            chartRef.current = chart;
          },
          // also fires after a legend click toggles a line: the y-axis
          // rescales, so any labels on screen are now in the wrong place
          updated: (chart) => {
            chartRef.current = chart;
            setLegendHover(null);
          },
        },
      },
      xaxis: {
        categories,
        tickPlacement: 'on',
      },
      yaxis: {
        labels: {
          formatter: (val) =>
            val >= 1000 ? `${(val / 1000).toFixed(0)}k` : `${Math.round(val)}`,
        },
        title: { text: metricLabel },
      },
      stroke: { curve: 'smooth', width: 2 },
      dataLabels: { enabled: false },
      legend: {
        position: 'right',
        width: 220,
        itemMargin: { vertical: 0 },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) => (val ? val.toLocaleString() : val),
        },
      },
    }),
    [categories, metricLabel],
  );

  // Hovering a legend entry labels that service line's value at every quarter,
  // right on its line (apex itself dims the other lines). Apex draws the
  // legend, so find the hovered entry by its class; its `rel` attribute is the
  // 1-based series index. Point positions come from apex's own layout.
  useEffect(() => {
    setLegendHover(null);
  }, [series]);
  // mousemove rather than mouseover, so the labels come back without leaving
  // the entry after a click toggles its line back on
  const onLegendMouseMove = (e) => {
    const item = e.target.closest('.apexcharts-legend-series');
    if (!item) return;
    const index = parseInt(item.getAttribute('rel'), 10) - 1;
    if (legendHover && legendHover.index === index) return;
    const chart = chartRef.current;
    const line = series[index];
    const globals = chart && chart.w && chart.w.globals;
    if (!line || !globals || !chartWrapRef.current || !chart.el) return;
    // a line toggled off in the legend has nothing to label
    if ((globals.collapsedSeriesIndices || []).includes(index)) return;
    const xs = globals.seriesXvalues[index] || [];
    const ys = globals.seriesYvalues[index] || [];
    // apex positions are relative to the plot area; shift them to our wrapper
    const wrapRect = chartWrapRef.current.getBoundingClientRect();
    const chartRect = chart.el.getBoundingClientRect();
    const originX = chartRect.left - wrapRect.left + globals.translateX;
    const originY = chartRect.top - wrapRect.top + globals.translateY;
    const points = [];
    line.data.forEach((val, qi) => {
      if (val === null || val === undefined) return;
      if (!Number.isFinite(xs[qi]) || !Number.isFinite(ys[qi])) return;
      points.push({
        key: categories[qi],
        left: originX + xs[qi],
        top: originY + ys[qi],
        text: val.toLocaleString(),
      });
    });
    setLegendHover({
      index,
      color: globals.colors[index],
      points,
    });
  };
  const onLegendMouseOut = (e) => {
    const item = e.target.closest('.apexcharts-legend-series');
    if (item && !item.contains(e.relatedTarget)) {
      setLegendHover(null);
    }
  };

  if (!entityKey) {
    return (
      <Card>
        <CardContent>
          <Typography className={classes.empty}>
            This record has no NPI or claims identifier, so no trending data is
            available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const loadedCount = entries.filter((e) => e && (e.loaded || e.error)).length;
  const isLoading = loadedCount === 0 || !servicelines;
  const allQuartersLoaded = loadedCount === entries.length;

  // One CSV row per quarter x serviceline x care setting for the servicelines
  // currently charted (Top 10 / All / one parent's sub-servicelines). Quarters
  // with no data file are simply absent — no carried-forward values here.
  const onExport = () => {
    const headers = [
      'Quarter',
      'Service Line',
      'Sub-Service Line',
      'Care Setting',
      metricLabel,
      'National Rank',
      'Territory Rank',
    ];
    const rows = [];
    entries.forEach((entry, qi) => {
      if (!entry || !entry.loaded || !entry.data) return;
      entry.data.forEach((row) => {
        if (!chartedSlugs.has(row.serviceline)) return;
        const names = servicelineNames.get(row.serviceline) || {
          serviceline: row.serviceline,
          subServiceline: '',
        };
        rows.push({
          quarterIndex: qi,
          quarter: categories[qi],
          serviceline: names.serviceline,
          subServiceline: names.subServiceline,
          careSetting:
            LocationTypeUtil.getName(row.setting) || row.setting_raw || '',
          patients:
            row.total_patients === null || row.total_patients === undefined
              ? SUPPRESSED_COUNT
              : row.total_patients,
          nationalRank: row.national_rank ?? '',
          territoryRank: row.territory_rank ?? '',
        });
      });
    });
    rows.sort(
      (a, b) =>
        a.serviceline.localeCompare(b.serviceline) ||
        a.subServiceline.localeCompare(b.subServiceline) ||
        a.quarterIndex - b.quarterIndex ||
        a.careSetting.localeCompare(b.careSetting),
    );
    const csv = [
      headers,
      ...rows.map((r) => [
        r.quarter,
        r.serviceline,
        r.subServiceline,
        r.careSetting,
        r.patients,
        r.nationalRank,
        r.territoryRank,
      ]),
    ]
      .map((fields) => fields.map(csvField).join(','))
      .join('\n');

    const entityName = (
      entity?.last_name
        ? `${entity.last_name}_${entity.first_name || ''}`
        : entity?.name || entityKey
    ).replace(/[^a-zA-Z0-9]+/g, '_');
    const metricSlug = metricLabel.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
    fileDownload(
      csv,
      `doctivity_${entityName}_${metricSlug}_BY_SERVICELINE_${format(new Date(), 'yy_MM_dd')}.csv`,
    );
  };
  const canExport = !isLoading && allQuartersLoaded && anyData && !noChildren;

  return (
    <Card>
      <CardContent>
        <TitleTooltip
          title={`${metricLabel} by Service Line`}
          tooltip={`Trailing-12-month ${metricLabel.toLowerCase()} per service line, by quarter. The download breaks each quarter out by care setting; suppressed counts are shown as <11.`}
          onExport={canExport ? onExport : undefined}
          rightLabel={
            allQuartersLoaded
              ? undefined
              : `Loading ${loadedCount}/${entries.length} quarters…`
          }
        />
        <div className={classes.filters}>
          <FormControl variant='outlined' className={classes.filter}>
            <InputLabel>Service Line</InputLabel>
            <Select
              value={servicelineSel}
              label='Service Line'
              onChange={(e) => setServicelineSel(e.target.value)}
            >
              <MenuItem value={TOP_10} className={classes.italicOption}>
                Top 10
              </MenuItem>
              <MenuItem value={ALL} className={classes.italicOption}>
                All
              </MenuItem>
              {availableParents.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Patient-type switch (pediatric / captured claims) parked for now —
              re-enable together with the patientType app-state line above.
          <div className={classes.filter}>
            <PatientTypeSelect />
          </div>
          */}
        </div>
        {isLoading ? (
          <LoadingView />
        ) : noChildren ? (
          <Typography className={classes.empty}>
            No Sub-Service Lines available for this Service Line
          </Typography>
        ) : !anyData ? (
          <Typography className={classes.empty}>
            No {metricLabel.toLowerCase()} data found for this{' '}
            {entityType === 'providers' ? 'provider' : 'organization'}
            {selectedParent ? ` in ${selectedParent.name}` : ''}.
          </Typography>
        ) : (
          <div
            ref={chartWrapRef}
            className={classes.chartWrap}
            onMouseMove={onLegendMouseMove}
            onMouseOut={onLegendMouseOut}
            onClick={() => setLegendHover(null)}
          >
            <Chart
              type='line'
              height={520}
              series={series}
              options={chartOptions}
            />
            {legendHover &&
              legendHover.points.map((point) => (
                <React.Fragment key={point.key}>
                  <span
                    className={classes.pointDot}
                    style={{
                      left: point.left,
                      top: point.top,
                      backgroundColor: legendHover.color,
                    }}
                  />
                  <span
                    className={classes.pointLabel}
                    style={{
                      left: point.left,
                      top: point.top,
                      borderColor: legendHover.color,
                    }}
                  >
                    {point.text}
                  </span>
                </React.Fragment>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

ServicelineTrendsChart.propTypes = {
  classes: PropTypes.object,
  entityType: PropTypes.oneOf(['providers', 'organizations']).isRequired,
  entity: PropTypes.object,
};

const Styled = withStyles(styles)(ServicelineTrendsChart);
export { Styled as ServicelineTrendsChart };
